import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type PieLabelRenderProps,
} from 'recharts';
import type { ReactNode } from 'react';

import { EmptyState } from '../../components/ui/Feedback';
import { reportChartColor } from './report-chart-colors';

export type ChartValueKind = 'amount' | 'number';
export type ReportChartDatum = { name: string } & Record<string, string | number | null | undefined>;

interface ChartSeries {
  key: string;
  name: string;
  color: string;
  stackId?: string;
}

interface TooltipEntry {
  color?: string;
  dataKey?: string | number;
  name?: string | number;
  value?: string | number;
}

const axisColor = '#6b817e';
const gridColor = '#e4eeec';

const formatNumber = (value: number): string => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
const formatValue = (value: number, kind: ChartValueKind): string => (kind === 'amount' ? `INR ${formatNumber(value)}` : formatNumber(value));
const shortValue = (value: string | number, kind: ChartValueKind): string => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (kind === 'amount') {
    if (Math.abs(numeric) >= 10000000) return `INR ${(numeric / 10000000).toFixed(1)}Cr`;
    if (Math.abs(numeric) >= 100000) return `INR ${(numeric / 100000).toFixed(1)}L`;
    if (Math.abs(numeric) >= 1000) return `INR ${(numeric / 1000).toFixed(1)}K`;
  }
  if (Math.abs(numeric) >= 100000) return `${(numeric / 100000).toFixed(1)}L`;
  if (Math.abs(numeric) >= 1000) return `${(numeric / 1000).toFixed(1)}K`;
  return formatNumber(numeric);
};

const numberFrom = (value: unknown): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const hasValues = (data: ReportChartDatum[], keys: string[]): boolean =>
  data.some((row) => keys.some((key) => numberFrom(row[key]) !== 0));

const ReportChartTooltip = ({ active, payload, label, valueKind = 'number' }: { active?: boolean; payload?: TooltipEntry[]; label?: string | number; valueKind?: ChartValueKind }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip report-chart-tooltip">
      {label !== undefined ? <strong>{label}</strong> : null}
      {payload.map((entry) => {
        const labelText = entry.name ?? entry.dataKey ?? 'Value';
        return (
          <div key={`${labelText}`} className="chart-tooltip-row">
            <span className="chart-tooltip-dot" style={{ background: entry.color ?? '#0d6b66' }} />
            <span>{labelText}</span>
            <b>{formatValue(numberFrom(entry.value), valueKind)}</b>
          </div>
        );
      })}
    </div>
  );
};

const ChartShell = ({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) => (
  <section className="chart-card report-analytics-card">
    <div className="section-heading report-analytics-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
    </div>
    <div className="chart-body report-analytics-body">{children}</div>
  </section>
);

export const ReportAnalyticsGrid = ({ children, columns = 2, className }: { children: ReactNode; columns?: 2 | 3; className?: string }) => (
  <div className={['report-analytics-grid', columns === 3 ? 'report-analytics-grid-3' : null, className].filter(Boolean).join(' ')}>{children}</div>
);

const PIE_RADIAN = Math.PI / 180;

// Percentage printed inside each pie slice; tiny slices are skipped so labels never overlap.
const renderPiePercentLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: PieLabelRenderProps) => {
  const share = Number(percent ?? 0);
  if (share < 0.04) return null;
  const inner = Number(innerRadius ?? 0);
  const radius = inner + (Number(outerRadius ?? 0) - inner) * 0.62;
  const angle = -Number(midAngle ?? 0) * PIE_RADIAN;
  return (
    <text x={Number(cx) + radius * Math.cos(angle)} y={Number(cy) + radius * Math.sin(angle)} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
      {`${(share * 100).toFixed(1)}%`}
    </text>
  );
};

export const ReportBarChartCard = ({
  eyebrow,
  title,
  data,
  bars,
  height = 260,
  indexKey = 'name',
  layout = 'horizontal',
  valueKind = 'number',
  emptyTitle = 'No chart data',
}: {
  eyebrow: string;
  title: string;
  data: ReportChartDatum[];
  bars: ChartSeries[];
  height?: number;
  indexKey?: string;
  layout?: 'horizontal' | 'vertical';
  valueKind?: ChartValueKind;
  emptyTitle?: string;
}) => {
  const keys = bars.map((bar) => bar.key);
  if (!hasValues(data, keys)) {
    return (
      <ChartShell eyebrow={eyebrow} title={title}>
        <EmptyState title={emptyTitle} message="Values will appear after report data is available." />
      </ChartShell>
    );
  }

  return (
    <ChartShell eyebrow={eyebrow} title={title}>
      <ResponsiveContainer width="100%" height={height}>
        {layout === 'vertical' ? (
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap="28%">
            <CartesianGrid horizontal={false} stroke={gridColor} />
            <XAxis type="number" tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} tickFormatter={(value: string | number) => shortValue(value, valueKind)} />
            <YAxis type="category" dataKey={indexKey} width={112} tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
            <Tooltip content={<ReportChartTooltip valueKind={valueKind} />} cursor={{ fill: 'rgba(13,107,102,.06)' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
            {bars.map((bar) => <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[0, 5, 5, 0]} maxBarSize={24} {...(bar.stackId ? { stackId: bar.stackId } : {})} />)}
          </BarChart>
        ) : (
          <BarChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 4 }} barCategoryGap="26%" barGap={4}>
            <CartesianGrid vertical={false} stroke={gridColor} />
            <XAxis dataKey={indexKey} tick={{ fontSize: 11, fill: axisColor }} axisLine={{ stroke: gridColor }} tickLine={false} interval={0} />
            <YAxis tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} width={54} tickFormatter={(value: string | number) => shortValue(value, valueKind)} />
            <Tooltip content={<ReportChartTooltip valueKind={valueKind} />} cursor={{ fill: 'rgba(13,107,102,.06)' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
            {bars.map((bar) => <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[5, 5, 0, 0]} maxBarSize={30} {...(bar.stackId ? { stackId: bar.stackId } : {})} />)}
          </BarChart>
        )}
      </ResponsiveContainer>
    </ChartShell>
  );
};

export const ReportDonutChartCard = ({
  eyebrow,
  title,
  data,
  valueKey = 'value',
  height = 260,
  valueKind = 'number',
  emptyTitle = 'No chart data',
  variant = 'donut',
}: {
  eyebrow: string;
  title: string;
  data: ReportChartDatum[];
  valueKey?: string;
  height?: number;
  valueKind?: ChartValueKind;
  emptyTitle?: string;
  variant?: 'donut' | 'pie';
}) => {
  const isPie = variant === 'pie';
  const chartData = data.filter((entry) => numberFrom(entry[valueKey]) !== 0);
  if (chartData.length === 0) {
    return (
      <ChartShell eyebrow={eyebrow} title={title}>
        <EmptyState title={emptyTitle} message="Values will appear after report data is available." />
      </ChartShell>
    );
  }

  return (
    <ChartShell eyebrow={eyebrow} title={title}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          {isPie ? (
            <Pie data={chartData} dataKey={valueKey} nameKey="name" outerRadius="82%" stroke="#fff" strokeWidth={1.5} label={renderPiePercentLabel} labelLine={false}>
              {chartData.map((entry, index) => <Cell key={entry.name} fill={typeof entry.color === 'string' ? entry.color : reportChartColor(index)} />)}
            </Pie>
          ) : (
            <Pie data={chartData} dataKey={valueKey} nameKey="name" innerRadius={58} outerRadius={86} paddingAngle={3} strokeWidth={0}>
              {chartData.map((entry, index) => <Cell key={entry.name} fill={typeof entry.color === 'string' ? entry.color : reportChartColor(index)} />)}
            </Pie>
          )}
          <Tooltip content={<ReportChartTooltip valueKind={valueKind} />} />
          <Legend verticalAlign={isPie ? 'top' : 'bottom'} wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
        </PieChart>
      </ResponsiveContainer>
    </ChartShell>
  );
};

export const ReportAreaChartCard = ({
  eyebrow,
  title,
  data,
  areas,
  height = 260,
  indexKey = 'name',
  valueKind = 'number',
  emptyTitle = 'No trend data',
}: {
  eyebrow: string;
  title: string;
  data: ReportChartDatum[];
  areas: ChartSeries[];
  height?: number;
  indexKey?: string;
  valueKind?: ChartValueKind;
  emptyTitle?: string;
}) => {
  const keys = areas.map((area) => area.key);
  if (!hasValues(data, keys)) {
    return (
      <ChartShell eyebrow={eyebrow} title={title}>
        <EmptyState title={emptyTitle} message="Trend values will appear after report data is available." />
      </ChartShell>
    );
  }

  return (
    <ChartShell eyebrow={eyebrow} title={title}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 6, right: 14, left: 0, bottom: 4 }}>
          <defs>
            {areas.map((area) => (
              <linearGradient key={area.key} id={`report-area-${area.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={area.color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={area.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke={gridColor} />
          <XAxis dataKey={indexKey} tick={{ fontSize: 11, fill: axisColor }} axisLine={{ stroke: gridColor }} tickLine={false} minTickGap={16} />
          <YAxis tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} width={54} tickFormatter={(value: string | number) => shortValue(value, valueKind)} />
          <Tooltip content={<ReportChartTooltip valueKind={valueKind} />} cursor={{ stroke: '#0d6b66', strokeWidth: 1, strokeDasharray: '4 4' }} />
          <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
          {areas.map((area) => (
            <Area key={area.key} type="monotone" dataKey={area.key} name={area.name} stroke={area.color} strokeWidth={2.2} fill={`url(#report-area-${area.key})`} dot={{ r: 2.6, strokeWidth: 0 }} activeDot={{ r: 4 }} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </ChartShell>
  );
};
